// CompletionDialog — modal for the "mark as completed" action with the
// 「撕信」 (tear-the-letter) animation per `docs/spec/wishlist/DESIGN.md
// §6.4` + `docs/spec/DESIGN.md §9 pattern 3`.
//
// Per spec:
//   - "純標為完成" → PATCH status='completed' → 撕信 animation → snackbar
//   - "寫一篇日記記錄這次完成" → POST /complete-with-diary → 撕信 animation
//     → navigate /diaries/{newId}
//
// The 撕信 animation is a CSS-only horizontal split: the dialog's body
// is cut down the middle by two `::after` pseudo-elements that grow
// apart + a diagonal `clip-path` rotation. Pure CSS keeps the animation
// declarative (no framer-motion dependency).
//
// W34.3.0: viewport-aware — mobile renders as BottomSheet variant="mobile"
// (90vh slide-up), desktop renders as BottomSheet variant="desktop-modal"
// (480px wide via className override). The 撕信 animation is desktop-only
// because the mobile sheet is too cramped for the two diagonal halves to
// read clearly; on mobile, the BottomSheet slide-down animation handles
// the dismiss instead. (Locked decision from W34.3 pre-grilling
// 2026-09-26.)

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, PenLine, X } from "lucide-react";
import { BottomSheet } from "@/components/patterns/BottomSheet";
import { cn } from "@/lib/utils/cn";
import { useSheetVariant } from "@/lib/hooks/useSheetVariant";
import { patchWish } from "@/lib/api/wishlist";
import { WISH_RESTORE_DAYS, retentionPhrase } from "@/lib/retentionWindows";
import { queryKeys } from "@/lib/queries/keys";

export interface CompletionDialogWish {
  id: string;
  name: string;
}

export interface CompletionDialogProps {
  /**
   * Target wish the dialog will act on. Pass the wish the user clicked
   * "Mark Complete" for. Passing the page's currently-loaded wish
   * unconditionally (instead of `null` when the dialog should be hidden)
   * is a long-standing footgun — the dialog used to derive its open state
   * from `wish !== null` and auto-open on every detail page load. Visibility
   * is now an explicit `open` prop. (W34.1 wishlist module verification.)
   */
  wish: CompletionDialogWish | null;
  /**
   * Whether the dialog is currently visible. The dialog only acts on
   * `wish` while `open === true`; otherwise it stays mounted (so the
   * tear animation can play out) but inert.
   */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after "純標為完成" completes so the parent can close the dialog + show snackbar. */
  onCompleted?: (wish: CompletionDialogWish) => void;
}

const ANIMATION_MS = 480;
const DESKTOP_WIDTH = 480;

export function CompletionDialog({
  wish,
  open,
  onOpenChange,
  onCompleted,
}: CompletionDialogProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tearing, setTearing] = useState(false);
  const { variant, isMobile } = useSheetVariant({
    desktopWidth: DESKTOP_WIDTH,
  });

  useEffect(() => {
    if (!open) setTearing(false);
  }, [open]);

  const markCompleteMut = useMutation({
    mutationFn: async () => {
      if (!wish) throw new Error("no wish");
      return patchWish(wish.id, { status: "completed" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wishes", "list"] });
      queryClient.invalidateQueries({ queryKey: ["wishes", "detail"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });

  const completeWithDiaryMut = useMutation({
    mutationFn: async () => {
      if (!wish) throw new Error("no wish");
      const res = await fetch(
        `/api/v1/wishes/${wish.id}/complete-with-diary`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({}),
        },
      );
      if (!res.ok) {
        const payload = (await res.json().catch(() => null)) as
          | { error?: { code?: string; message?: string } }
          | null;
        throw new Error(payload?.error?.message ?? "標記完成失敗，請稍後再試。");
      }
      return (await res.json()) as { diary_id: string };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["wishes", "list"] });
      queryClient.invalidateQueries({ queryKey: ["wishes", "detail"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      const diaryId = data?.diary_id;
      if (diaryId) navigate(`/diaries/${diaryId}`);
    },
  });

  const onPlainComplete = () => {
    if (!wish) return;
    setTearing(true);
    setTimeout(async () => {
      try {
        await markCompleteMut.mutateAsync();
        onCompleted?.(wish);
      } finally {
        setTearing(false);
        onOpenChange(false);
      }
    }, ANIMATION_MS);
  };

  const onCompleteWithDiary = () => {
    if (!wish) return;
    setTearing(true);
    setTimeout(async () => {
      try {
        await completeWithDiaryMut.mutateAsync();
        onOpenChange(false);
      } catch {
        setTearing(false);
      }
    }, ANIMATION_MS);
  };

  // W34.3.0 anti-drift: 撕信 animation is desktop-only. On mobile, the
  // sheet's own slide-down handles the dismiss — the viewport is too
  // cramped for two diagonal halves to read.
  const tearAllowed = !isMobile;

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      variant={variant}
      // Override BottomSheet's default 520px width on desktop so the
      // CompletionDialog matches its v1 card geometry (480px).
      // Mobile sheet ignores this className — BottomSheet's mobile
      // variant anchors to the bottom regardless.
      className={
        isMobile
          ? "max-h-[90vh] overflow-y-auto pb-[max(1.25rem,env(safe-area-inset-bottom))]"
          : "!w-[480px]"
      }
    >
      <div
        data-component="CompletionDialog"
        data-testid="completion-dialog"
        data-open={open}
        data-variant={isMobile ? "mobile" : "desktop"}
        data-tearing={tearing ? "true" : "false"}
        // When closed, fully unmount the body so it doesn't bleed through
        // the empty overlay. `tearing` keeps the body mounted during the
        // tear-letter exit animation so the animation can finish.
        // (W34.1 wishlist bugfix, preserved through the W34.3 migration.)
        hidden={!open && !tearing}
        className={cn(
          "flex flex-col gap-s-3 relative",
          // Only render the card chrome on desktop. The mobile sheet
          // already provides the surface (glass-1) and padding.
          !isMobile &&
            "bg-paper-elevated border border-hairline rounded-2xl shadow-modal p-s-6 transition-transform duration-280",
          !isMobile && (open ? "scale-100" : "scale-95"),
        )}
        style={
          // Belt-and-suspenders width override (BottomSheet also reads
          // its className). 480px is the W34.3.0 desktop width per
          // design decision.
          !isMobile ? { width: `${DESKTOP_WIDTH}px`, maxWidth: "92vw" } : undefined
        }
      >
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          disabled={tearing}
          aria-label="關閉"
          className="press absolute right-s-3 top-s-3 min-h-11 min-w-11 flex items-center justify-center rounded-md text-ink-2 hover:bg-paper-deep"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
        <h2
          id="completion-dialog-title"
          className="font-display text-title text-ink-1 pr-12"
        >
          標為完成
        </h2>
        {wish ? (
          <p className="text-body text-ink-2">
            確定將「<span className="font-semibold text-ink-1">{wish.name}</span>
            」標記為完成？
          </p>
        ) : null}
        <div className="flex flex-col gap-s-2 mt-s-2">
          <button
            type="button"
            onClick={onPlainComplete}
            disabled={tearing || !wish || markCompleteMut.isPending}
            data-testid="completion-dialog-plain"
            className="press w-full min-h-12 flex items-center justify-center gap-2 rounded-full bg-ink-1 text-paper text-body-sm font-semibold disabled:opacity-50"
          >
            <Check className="h-4 w-4" aria-hidden />
            純標為完成
          </button>
          <button
            type="button"
            onClick={onCompleteWithDiary}
            disabled={tearing || !wish || completeWithDiaryMut.isPending}
            data-testid="completion-dialog-diary"
            className="press w-full min-h-12 flex items-center justify-center gap-2 rounded-full border border-hairline text-ink-1 text-body-sm font-semibold disabled:opacity-50"
          >
            <PenLine className="h-4 w-4" aria-hidden />
            寫一篇日記記錄這次完成
          </button>
        </div>
        <p className="text-body-sm text-ink-3">
          完成後心願會移至 archive（{retentionPhrase(WISH_RESTORE_DAYS)}復原）
        </p>
        {/* 撕信 animation overlay (CSS-only, desktop-only per W34.3.0):
            two diagonal halves slide apart + a paper-fold clip-path.
            Per `docs/spec/DESIGN.md §9 pattern 3`. */}
        {tearing && tearAllowed ? (
          <div
            data-testid="completion-dialog-tear"
            className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl"
          >
            <div className="absolute inset-y-0 left-0 w-1/2 bg-paper origin-right animate-tear-left" />
            <div className="absolute inset-y-0 right-0 w-1/2 bg-paper origin-left animate-tear-right" />
          </div>
        ) : null}
      </div>
    </BottomSheet>
  );
}